import { DependencyList, EffectCallback, useEffect, useRef } from 'react';

/**
 * useDidUpdate
 *
 * A custom React hook that runs a given effect **only** after the initial render,
 * i.e., it skips execution on mount and only responds to subsequent updates
 * based on the provided dependency list.
 *
 * This is useful for scenarios where you want to react to changes in state or props
 * but do **not** want the effect to run during the initial render (such as
 * triggering animations, analytics events, or data syncs that shouldn't happen on mount).
 *
 * @param effect - The effect callback function to execute after updates.
 *                 It can optionally return a cleanup function.
 * @param deps - An optional array of dependencies for the effect.
 *               The effect will re-run only if one of these values changes.
 */
export function useDidUpdate(effect: EffectCallback, deps: DependencyList = []) {
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return undefined;
        }

        return effect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
